import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { P11, PRIVACY_URL, TERMS_URL, BgVideo, Particles } from "./p11shared";
import { useP11Lang } from "./p11lang";
import { PixelField, TechnicalGlyph, useP11Sound } from "./p11fx";

const REF_DATE = "2026-11-03"; // 18+ op eerste trainingsdatum
const STEPS = 6;
const STEP_GLYPHS = ["crosshair", "marker", "nodes", "signal", "square", "check"];

const empty = {
  first_name: "", last_name: "", dob: "", phone: "", email: "",
  street: "", house_number: "", house_addition: "", postal_code: "", city: "",
  ec_name: "", ec_relation: "", ec_phone: "",
  experience: null, goals: [], goals_other: "", injuries: null, injuries_text: "",
  extra_notes: "",
  c_terms: false, c_privacy: false, c_health: false, c_media: false, c_marketing: false, commitment: false,
};

function isEligible(dob) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return null; // onvolledig → geen oordeel
  const d = new Date(`${dob}T00:00:00`);
  if (isNaN(d) || d > new Date() || d.getFullYear() < 1900) return null;
  const ref = new Date(`${REF_DATE}T00:00:00`);
  const turned18 = new Date(d); turned18.setFullYear(d.getFullYear() + 18);
  return turned18 <= ref;
}

// NB: module-level componenten — binnen de pagina gedefinieerde inputs verliezen
// bij elke toetsaanslag hun focus omdat React ze dan opnieuw mount.
function Field({ k, label, type = "text", required = false, autoComplete, inputMode, testid, value, onChange, error }) {
  return (
    <div>
      <label className="p11-label" htmlFor={testid}>{label}{required ? " *" : ""}</label>
      <input id={testid} type={type} value={value} autoComplete={autoComplete} inputMode={inputMode}
        aria-invalid={!!error} aria-describedby={error ? `${testid}-error` : undefined}
        onChange={(e) => onChange(k, e.target.value)} className={`p11-input mt-1${error ? " p11-input-error" : ""}`} data-testid={testid} />
      {error && <p className="p11-error" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

function Check({ k, label, required = false, link, checked, onChange, lang, error }) {
  return (
    <div>
      <label className="flex items-start gap-3 cursor-pointer py-1" data-testid={`p11-consent-${k}-wrap`}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(k, e.target.checked)} className="mt-1 w-5 h-5 accent-[#FFE44D] shrink-0" data-testid={`p11-consent-${k}`} />
        <span className="text-sm leading-relaxed" style={{ color: P11.gray2 }}>
          {label}{required ? " *" : ""}{link && <> <a href={link} target="_blank" rel="noreferrer" style={{ color: P11.yellow }} className="underline underline-offset-2" data-testid={`p11-consent-${k}-link`}>({lang === "nl" ? "lees" : "read"})</a></>}
        </span>
      </label>
      {error && <p className="p11-error" data-testid={`p11-consent-${k}-error`}>{error}</p>}
    </div>
  );
}

export default function P11Register() {
  const nav = useNavigate();
  const { lang, tt } = useP11Lang();
  const { play } = useP11Sound();
  const DRAFT_KEY = "p11_reg_draft";
  // concept herstellen uit de huidige browser-sessie (sessionStorage — geen localStorage,
  // geen auth-data; verdwijgt automatisch bij sluiten van het tabblad)
  const [restored] = useState(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (d && typeof d === "object" && d.f && typeof d.f === "object") {
        return { f: { ...empty, ...d.f }, step: Number.isInteger(d.step) && d.step >= 1 && d.step <= STEPS ? d.step : 1 };
      }
    } catch { /* onbruikbare draft negeren */ }
    return null;
  });
  const [step, setStep] = useState(restored?.step || 1);
  const [f, setF] = useState(restored?.f || empty);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({}); // veld-niveau foutmeldingen { veld: code }
  const [phase, setPhase] = useState("form"); // form | processing | received
  const [savedTick, setSavedTick] = useState(false); // [ VOORTGANG OPGESLAGEN ]
  const [checking, setChecking] = useState(() => !!localStorage.getItem("p11_token"));
  const set = (k, v) => {
    setF((p) => ({ ...p, [k]: v }));
    setErrors((p) => (p[k] ? (({ [k]: _drop, ...rest }) => rest)(p) : p));
  };

  // slimme navigatie: al (deels) ingeschreven met account? → nooit opnieuw registreren
  useEffect(() => {
    const token = localStorage.getItem("p11_token");
    if (!token) return;
    api.get("/project11/me/dashboard").then(({ data }) => {
      if (data?.state && !["REJECTED", "CANCELLED"].includes(data.state)) {
        nav("/project11/dashboard", { replace: true });
      } else setChecking(false);
    }).catch((err) => {
      if (err?.response?.status === 401 || err?.response?.status === 403) localStorage.removeItem("p11_token");
      setChecking(false);
    });
  }, [nav]);

  // autosave van het concept in de sessie (gedebounced) + subtiele bevestiging
  const skipSave = useRef(true);
  useEffect(() => {
    if (phase !== "form") return;
    if (skipSave.current) { skipSave.current = false; return; }
    const t = setTimeout(() => {
      try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ f, step })); } catch { /* sessie vol — nooit blokkeren */ }
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1600);
    }, 700);
    return () => clearTimeout(t);
  }, [f, step, phase]);

  const eligible = useMemo(() => isEligible(f.dob), [f.dob]);
  const goalsList = tt("goals_list");
  const otherLabel = lang === "nl" ? "Anders" : "Other";

  // foutcode → tweetalige melding
  const msg = (code) => ({
    required: tt("err_required"), invalid_email: tt("err_email"), invalid_dob: tt("err_dob"),
    underage: tt("err_underage"), taken: tt("err_taken"), goals: tt("err_goals"),
    choice: tt("err_choice"), consent: tt("err_consent"),
  }[code] || tt("err_required"));
  const errMsg = (k) => (errors[k] ? msg(errors[k]) : null);

  // welk veld hoort bij welke stap (voor terugspringen bij server-fouten)
  const FIELD_STEP = {
    first_name: 1, last_name: 1, dob: 1, phone: 1, email: 1,
    street: 2, house_number: 2, house_addition: 2, postal_code: 2, city: 2,
    ec_name: 3, ec_relation: 3, ec_phone: 3,
    experience: 4, goals: 4, injuries: 4, injuries_text: 4,
    extra_notes: 5, c_terms: 6, c_privacy: 6, c_health: 6, commitment: 6,
  };

  // per-stap validatie → { veld: foutcode }
  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!f.first_name.trim()) e.first_name = "required";
      if (!f.last_name.trim()) e.last_name = "required";
      if (!f.dob.trim()) e.dob = "required";
      else if (eligible === null) e.dob = "invalid_dob";
      else if (eligible === false) e.dob = "underage";
      if (!f.phone.trim()) e.phone = "required";
      if (!f.email.trim()) e.email = "required";
      else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "invalid_email";
    }
    if (s === 3) {
      if (!f.ec_name.trim()) e.ec_name = "required";
      if (!f.ec_relation.trim()) e.ec_relation = "required";
      if (!f.ec_phone.trim()) e.ec_phone = "required";
    }
    if (s === 4) {
      if (f.experience === null) e.experience = "choice";
      if (f.goals.length === 0) e.goals = "goals";
      if (f.injuries === null) e.injuries = "choice";
      else if (f.injuries && !f.injuries_text.trim()) e.injuries_text = "required";
    }
    if (s === 6) {
      if (!f.c_terms) e.c_terms = "consent";
      if (!f.c_privacy) e.c_privacy = "consent";
      if (!f.c_health) e.c_health = "consent";
      if (!f.commitment) e.commitment = "consent";
    }
    return e;
  };

  const tryAdvance = () => {
    const e = validateStep(step);
    if (Object.keys(e).length) { setErrors(e); play("error"); return; }
    setErrors({});
    play("confirm");
    setStep(step + 1);
  };

  const submit = async () => {
    const local = validateStep(6);
    if (Object.keys(local).length) { setErrors(local); play("error"); return; }
    setBusy(true);
    setPhase("processing");
    play("processing"); // eenmalig laadgeluid bij indienen
    const started = Date.now();
    try {
      const { data } = await api.post("/project11/auth/register", {
        first_name: f.first_name, last_name: f.last_name, dob: f.dob, phone: f.phone, email: f.email,
        address: { street: f.street, house_number: f.house_number, house_addition: f.house_addition, postal_code: f.postal_code, city: f.city },
        emergency_contact: { name: f.ec_name, relation: f.ec_relation, phone: f.ec_phone },
        experience: f.experience, goals: f.goals, goals_other: f.goals_other,
        injuries: f.injuries, injuries_text: f.injuries_text,
        extra_notes: f.extra_notes,
        consents: { terms: f.c_terms, privacy: f.c_privacy, health_truth: f.c_health, media: f.c_media, marketing: f.c_marketing },
        commitment: f.commitment, language: lang,
      });
      // auto-login: deelnemer landt direct op zijn dashboard (status IN BEOORDELING tot admin-goedkeuring)
      if (data.token) localStorage.setItem("p11_token", data.token);
      // laat het processing-moment kort maar zichtbaar zijn (geen kunstmatige lange delay)
      const wait = Math.max(0, 1600 - (Date.now() - started));
      setTimeout(() => {
        setBusy(false);
        try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
        setPhase("received"); // auto-login token is opgeslagen → dashboard direct bereikbaar
      }, wait);
    } catch (err) {
      setPhase("form");
      setBusy(false);
      const d = err?.response?.data?.detail;
      if (d === "UNDERAGE") {
        setErrors({ dob: "underage" });
        setStep(1);
        toast.error(lang === "nl" ? "Minimumleeftijd is 18 jaar." : "Minimum age is 18.");
      } else if (d === "ALREADY_REGISTERED") {
        setErrors({ email: "taken" });
        setStep(1);
        toast.error(lang === "nl" ? "Dit e-mailadres is al ingeschreven." : "This email is already registered.");
      } else if (d && typeof d === "object" && d.errors) {
        // veld-niveau fouten van de server → markeer de velden en spring naar de juiste stap
        setErrors(d.errors);
        const steps = Object.keys(d.errors).map((k) => FIELD_STEP[k] || 1);
        setStep(Math.min(...steps));
        play("error");
      } else {
        toast.error(typeof d === "string" ? d : (lang === "nl" ? "Inschrijven mislukt" : "Registration failed"));
      }
    }
  };

  if (checking) {
    return (
      <div className="p11-root relative flex items-center justify-center min-h-screen" data-testid="p11-reg-checking">
        <span className="p11-label">PROJECT 11* …</span>
      </div>
    );
  }

  if (phase === "processing") {
    return (
      <div className="p11-root relative" data-testid="p11-register-processing">
        <BgVideo />
      <Particles density={40} />
        <main className="relative z-10 min-h-screen flex flex-col items-center justify-center text-center px-6">
          <div className="p11-label mb-2">PROJECT 11* / {lang === "nl" ? "INSCHRIJFSYSTEEM" : "REGISTRATION SYSTEM"}</div>
          <div className="p11-h2 text-2xl md:text-3xl mt-8 p11-blink" style={{ color: P11.text }} data-testid="p11-processing-title">{tt("processing_title")}</div>
          <div className="p11-label-yellow mt-4">{tt("processing_sub")}</div>
          <div className="mt-10"><PixelField size={120} density={9} testid="p11-processing-pixels" /></div>
          <div className="mt-10 w-48 h-px mx-auto overflow-hidden" style={{ background: P11.border }}>
            <div className="h-full w-1/3" style={{ background: P11.yellow, animation: "p11scan 1.2s linear infinite", position: "relative" }} />
          </div>
        </main>
      </div>
    );
  }

  if (phase === "received") {
    return (
      <div className="p11-root relative" data-testid="p11-register-received">
        <BgVideo />
      <Particles density={40} />
        <main className="relative z-10 min-h-screen flex flex-col justify-center px-6 max-w-xl mx-auto">
          <div className="flex items-center gap-3 p11-label-yellow" data-testid="p11-received-label"><TechnicalGlyph type="check" size={15} active />{tt("received_label")}</div>
          <h1 className="p11-h1 text-4xl md:text-5xl mt-8" data-testid="p11-received-title">{tt("received_title")}</h1>
          <p className="p11-mono text-sm mt-8" style={{ color: P11.gray2 }} data-testid="p11-received-id">{tt("received_id")}</p>
          <div className="mt-4 flex items-center gap-3 border px-4 py-3" style={{ borderColor: P11.yellow }} data-testid="p11-received-status">
            <span className="w-1.5 h-1.5 p11-blink" style={{ background: P11.yellow }} />
            <span className="p11-label-yellow">{tt("received_status")}</span>
          </div>
          <p className="mt-6 text-base leading-relaxed" style={{ color: P11.gray2 }} data-testid="p11-received-body">{tt("received_body")}</p>
          <p className="mt-4 text-sm" style={{ color: P11.gray }}>{tt("received_note")}</p>
          <div className="mt-12 flex items-center gap-6 self-start">
            <button onClick={() => nav("/project11/dashboard")} className="p11-btn" data-testid="p11-received-dashboard">{tt("received_dashboard")}</button>
            <button onClick={() => nav("/project11")} className="p11-label hover:text-white transition-colors" data-testid="p11-received-home">{tt("back_home")}</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="p11-root relative" data-testid="p11-register">
      <BgVideo />
      <Particles density={40} />
      <div className="relative z-10 max-w-xl mx-auto px-6 py-10 min-h-screen flex flex-col">
        {/* header + voortgang */}
        <div className="flex items-center justify-between p11-label mb-3">
          <button onClick={() => { play("click"); step === 1 ? nav("/project11") : setStep(step - 1); }} className="hover:text-white transition-colors" data-testid="p11-reg-back">{tt("back")}</button>
          <span className="inline-flex items-center gap-2" data-testid="p11-reg-progress">
            <TechnicalGlyph type={STEP_GLYPHS[step - 1]} size={13} active />
            {tt("reg_of")} / <span style={{ color: P11.yellow }}>{String(step).padStart(2, "0")}</span>—0{STEPS}
          </span>
        </div>
        {/* concept-status + autosave-bevestiging */}
        <div className="flex items-center justify-end gap-4 mb-2 min-h-[14px]">
          <span className="p11-label" style={{ color: P11.gray }} data-testid="p11-reg-draft-badge">[ {tt("draft_badge")} ]</span>
          {savedTick && <span className="p11-label-yellow p11-fade-in" data-testid="p11-reg-saved">{tt("draft_saved")}</span>}
        </div>
        <div className="flex gap-2 mb-10" data-testid="p11-reg-dots">
          {Array.from({ length: STEPS }, (_, i) => (
            <div key={i} className="w-5 h-5 border flex items-center justify-center"
              style={{ borderColor: i + 1 === step ? P11.yellow : i + 1 < step ? P11.yellow : P11.border, background: i + 1 < step ? "rgba(255,228,77,0.12)" : "transparent" }}
              data-testid={`p11-reg-step-square-${i + 1}`}>
              {i + 1 < step
                ? <TechnicalGlyph type="check" size={10} active />
                : <span className="p11-mono" style={{ fontSize: 8, color: i + 1 === step ? P11.yellow : P11.gray }}>{i + 1}</span>}
            </div>
          ))}
        </div>

        <div className="flex-1">
          {step === 1 && (
            <section className="p11-fade-in space-y-7" data-testid="p11-step-1">
              <h1 className="p11-h2 text-2xl">{tt("s1_title")}</h1>
              <Field k="first_name" value={f.first_name} onChange={set} label={tt("first_name")} required autoComplete="given-name" testid="p11-f-first-name" error={errMsg("first_name")} />
              <Field k="last_name" value={f.last_name} onChange={set} label={tt("last_name")} required autoComplete="family-name" testid="p11-f-last-name" error={errMsg("last_name")} />
              <Field k="dob" value={f.dob} onChange={set} label={tt("dob")} required type="date" autoComplete="bday" testid="p11-f-dob" error={errMsg("dob")} />
              {eligible === false && (
                <div className="border p-6 p11-fade-in" style={{ borderColor: P11.yellow }} data-testid="p11-underage">
                  <div className="p11-h2 text-lg" style={{ color: P11.yellow }}>{tt("underage_title")}</div>
                  <p className="text-sm mt-3 leading-relaxed" style={{ color: P11.gray2 }}>{tt("underage_body")}</p>
                  <a href="mailto:team@johboxing.com" className="p11-btn-ghost mt-5" data-testid="p11-underage-mail">{tt("send_email")}</a>
                </div>
              )}
              <Field k="phone" value={f.phone} onChange={set} label={tt("phone")} required type="tel" autoComplete="tel" inputMode="tel" testid="p11-f-phone" error={errMsg("phone")} />
              <Field k="email" value={f.email} onChange={set} label={tt("email")} required type="email" autoComplete="email" inputMode="email" testid="p11-f-email" error={errMsg("email")} />
            </section>
          )}

          {step === 2 && (
            <section className="p11-fade-in space-y-7" data-testid="p11-step-2">
              <h1 className="p11-h2 text-2xl">{tt("s2_title")}</h1>
              <p className="text-sm" style={{ color: P11.gray }}>{tt("optional_skip")}</p>
              <Field k="street" value={f.street} onChange={set} label={tt("street")} autoComplete="address-line1" testid="p11-f-street" />
              <div className="grid grid-cols-2 gap-6">
                <Field k="house_number" value={f.house_number} onChange={set} label={tt("house_number")} inputMode="numeric" testid="p11-f-house-number" />
                <Field k="house_addition" value={f.house_addition} onChange={set} label={tt("house_addition")} testid="p11-f-house-addition" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <Field k="postal_code" value={f.postal_code} onChange={set} label={tt("postal_code")} autoComplete="postal-code" testid="p11-f-postal" />
                <Field k="city" value={f.city} onChange={set} label={tt("city")} autoComplete="address-level2" testid="p11-f-city" />
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="p11-fade-in space-y-7" data-testid="p11-step-3">
              <h1 className="p11-h2 text-2xl">{tt("s3_title")}</h1>
              <p className="text-sm" style={{ color: P11.gray }}>{tt("ec_note")}</p>
              <Field k="ec_name" value={f.ec_name} onChange={set} label={tt("ec_name")} required testid="p11-f-ec-name" error={errMsg("ec_name")} />
              <Field k="ec_relation" value={f.ec_relation} onChange={set} label={tt("ec_relation")} required testid="p11-f-ec-relation" error={errMsg("ec_relation")} />
              <Field k="ec_phone" value={f.ec_phone} onChange={set} label={tt("ec_phone")} required type="tel" inputMode="tel" testid="p11-f-ec-phone" error={errMsg("ec_phone")} />
            </section>
          )}

          {step === 4 && (
            <section className="p11-fade-in space-y-8" data-testid="p11-step-4">
              <h1 className="p11-h2 text-2xl">{tt("s4_title")}</h1>
              <div>
                <div className="p11-label mb-3">{tt("exp_q")} *</div>
                <div className="flex gap-3">
                  {[true, false].map((v) => (
                    <button key={String(v)} onClick={() => set("experience", v)} className="p11-btn-ghost" style={f.experience === v ? { borderColor: P11.yellow, color: P11.yellow } : {}} data-testid={`p11-f-experience-${v ? "yes" : "no"}`}>{v ? tt("yes") : tt("no")}</button>
                  ))}
                </div>
                {errMsg("experience") && <p className="p11-error" data-testid="p11-f-experience-error">{errMsg("experience")}</p>}
              </div>
              <div>
                <div className="p11-label mb-3">{tt("goals_q")} *</div>
                <div className="space-y-2">
                  {goalsList.map((g) => (
                    <label key={g} className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" checked={f.goals.includes(g)} onChange={(e) => set("goals", e.target.checked ? [...f.goals, g] : f.goals.filter((x) => x !== g))} className="accent-[#FFE44D]" data-testid={`p11-f-goal-${g}`} />
                      <span className="text-sm" style={{ color: P11.gray2 }}>{g}</span>
                    </label>
                  ))}
                </div>
                {f.goals.includes(otherLabel) && (
                  <input value={f.goals_other} onChange={(e) => set("goals_other", e.target.value)} placeholder={tt("goals_other")} className="p11-input mt-3" data-testid="p11-f-goals-other" />
                )}
                {errMsg("goals") && <p className="p11-error" data-testid="p11-f-goals-error">{errMsg("goals")}</p>}
              </div>
              <div>
                <div className="p11-label mb-3">{tt("injuries_q")} *</div>
                <div className="flex gap-3">
                  <button onClick={() => set("injuries", false)} className="p11-btn-ghost" style={f.injuries === false ? { borderColor: P11.yellow, color: P11.yellow } : {}} data-testid="p11-f-injuries-no">{tt("no")}</button>
                  <button onClick={() => set("injuries", true)} className="p11-btn-ghost" style={f.injuries === true ? { borderColor: P11.yellow, color: P11.yellow } : {}} data-testid="p11-f-injuries-yes">{tt("injuries_yes")}</button>
                </div>
                {f.injuries === true && (
                  <div>
                    <textarea value={f.injuries_text} onChange={(e) => set("injuries_text", e.target.value)} rows={3} className={`p11-input mt-3${errors.injuries_text ? " p11-input-error" : ""}`} data-testid="p11-f-injuries-text" />
                    {errMsg("injuries_text") && <p className="p11-error" data-testid="p11-f-injuries-text-error">{errMsg("injuries_text")}</p>}
                  </div>
                )}
                {errMsg("injuries") && <p className="p11-error" data-testid="p11-f-injuries-error">{errMsg("injuries")}</p>}
                <p className="text-xs mt-3" style={{ color: P11.gray }}>{tt("injuries_why")}</p>
              </div>
            </section>
          )}

          {step === 5 && (
            <section className="p11-fade-in space-y-7" data-testid="p11-step-5">
              <h1 className="p11-h2 text-2xl">{tt("s5_title")}</h1>
              <div>
                <label className="p11-label">{tt("extra_notes")}</label>
                <textarea value={f.extra_notes} onChange={(e) => set("extra_notes", e.target.value.slice(0, 500))} rows={5} maxLength={500} className="p11-input mt-1" data-testid="p11-f-extra-notes" />
                <div className="p11-label mt-1 text-right">{f.extra_notes.length}/500</div>
              </div>
            </section>
          )}

          {step === 6 && (
            <section className="p11-fade-in space-y-8" data-testid="p11-step-6">
              <h1 className="p11-h2 text-2xl">{tt("s6_title")}</h1>
              <div className="space-y-4">
                <Check k="c_terms" checked={f.c_terms} onChange={set} lang={lang} label={tt("c_terms")} required link={TERMS_URL} error={errMsg("c_terms")} />
                <Check k="c_privacy" checked={f.c_privacy} onChange={set} lang={lang} label={tt("c_privacy")} required link={PRIVACY_URL} error={errMsg("c_privacy")} />
                <Check k="c_health" checked={f.c_health} onChange={set} lang={lang} label={tt("c_health")} required error={errMsg("c_health")} />
                <Check k="c_media" checked={f.c_media} onChange={set} lang={lang} label={tt("c_media")} />
                <Check k="c_marketing" checked={f.c_marketing} onChange={set} lang={lang} label={tt("c_marketing")} />
              </div>
              <div className="border p-6" style={{ borderColor: P11.border }} data-testid="p11-commitment">
                <div className="p11-h2 text-base" style={{ color: P11.yellow }}>{tt("commitment_title")}</div>
                <p className="text-sm mt-3" style={{ color: P11.gray2 }}>{tt("commitment_body")}</p>
                <div className="mt-4"><Check k="commitment" checked={f.commitment} onChange={set} lang={lang} label={tt("commitment_check")} required error={errMsg("commitment")} /></div>
              </div>
              <div data-testid="p11-review">
                <div className="p11-label-yellow mb-4">{tt("review_title")}</div>
                <div className="space-y-2 text-sm" style={{ color: P11.gray2 }}>
                  {[[1, `${f.first_name} ${f.last_name} · ${f.dob}`], [1, `${f.email} · ${f.phone}`],
                   [2, [f.street, f.house_number, f.house_addition, f.postal_code, f.city].filter(Boolean).join(" ") || "—"],
                   [3, `${f.ec_name} (${f.ec_relation}) · ${f.ec_phone}`],
                   [4, `${tt("exp_q")}: ${f.experience ? tt("yes") : tt("no")} · ${f.goals.join(", ")}`],
                   [5, f.extra_notes || "—"]].map(([s, text], i) => (
                    <div key={i} className="flex items-start justify-between gap-4 border-b pb-2" style={{ borderColor: P11.border }}>
                      <span className="break-words">{text}</span>
                      <button onClick={() => setStep(s)} className="p11-label hover:text-white shrink-0" data-testid={`p11-review-edit-${s}`}>{tt("edit")}</button>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <div className="pt-10">
          {step < 6 ? (
            <button onClick={tryAdvance} className="p11-btn w-full" data-testid="p11-reg-continue">{tt("continue")}</button>
          ) : (
            <button onClick={submit} disabled={busy} className="p11-btn w-full" data-testid="p11-reg-submit">{busy ? "…" : tt("complete_reg")}</button>
          )}
        </div>
      </div>
    </div>
  );
}
